import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import config from '../../config';
import makeRequest from '../../makeRequest';

const BASE = config.api.baseUrl;

/* ─── Injected styles ───────────────────────────────────────────── */
const STYLE_ID = 'svc-details-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes svcFadeUp {
      from { opacity: 0; transform: translateY(24px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes svcShimmer {
      0%   { background-position: -700px 0; }
      100% { background-position: 700px 0; }
    }
    @keyframes svcPulse {
      0%, 100% { transform: scale(1); }
      50%       { transform: scale(1.04); }
    }

    /* ── Prose content ── */
    .svc-html h1,.svc-html h2,.svc-html h3,
    .svc-html h4,.svc-html h5,.svc-html h6 {
      font-weight:700; line-height:1.3; margin:1.4em 0 .5em; color:#0f172a;
    }
    .svc-html h1{font-size:2rem;}
    .svc-html h2{font-size:1.6rem;}
    .svc-html h3{font-size:1.35rem;}
    .svc-html h4{font-size:1.15rem;}
    .svc-html p{margin:0 0 1.1em;line-height:1.8;color:#334155;}
    .svc-html ul,.svc-html ol{margin:0 0 1.1em 1.5em;padding:0;color:#334155;line-height:1.8;}
    .svc-html li{margin-bottom:.35em;}
    .svc-html blockquote{
      border-left:4px solid #076cec;margin:1.5em 0;padding:.75em 1.2em;
      background:#f0f7ff;color:#334155;font-style:italic;border-radius:0 8px 8px 0;
    }
    .svc-html strong{font-weight:700;}
    .svc-html em{font-style:italic;}
    .svc-html a{color:#076cec;text-decoration:underline;}
    .svc-html table{width:100%;border-collapse:collapse;margin:1em 0;}
    .svc-html th,.svc-html td{border:1px solid #e2e8f0;padding:8px 12px;text-align:left;}
    .svc-html th{background:#f1f5f9;font-weight:600;}
    .svc-html hr{border:none;border-top:1px solid #e2e8f0;margin:2em 0;}
    .svc-html img{max-width:100%;border-radius:10px;}
    .svc-html code{background:#f0f4fa;padding:2px 6px;border-radius:4px;font-size:.88em;}
  `;
  document.head.appendChild(style);
}

/* ─── Icon map ──────────────────────────────────────────────────── */
const ICON_MAP = [
  { keys: ['cardio', 'heart'], icon: 'fa-solid fa-heart-pulse' },
  { keys: ['dental', 'tooth'], icon: 'fa-solid fa-tooth' },
  { keys: ['neurol', 'brain'], icon: 'fa-solid fa-brain' },
  { keys: ['ortho', 'bone', 'joint'], icon: 'fa-solid fa-bone' },
  { keys: ['paediat', 'pediatr', 'child'], icon: 'fa-solid fa-baby' },
  { keys: ['gynae', 'gynec', 'obstet', 'maternity'], icon: 'fa-solid fa-venus' },
  { keys: ['eye', 'ophthal'], icon: 'fa-solid fa-eye' },
  { keys: ['uro', 'kidney'], icon: 'fa-solid fa-kidneys' },
  { keys: ['pulmon', 'lung', 'respirat'], icon: 'fa-solid fa-lungs' },
  { keys: ['surge', 'operat'], icon: 'fa-solid fa-scalpel' },
  { keys: ['oncol', 'cancer'], icon: 'fa-solid fa-ribbon' },
  { keys: ['haemat', 'hemat', 'blood'], icon: 'fa-solid fa-droplet' },
  { keys: ['internal', 'general'], icon: 'fa-solid fa-stethoscope' },
  { keys: ['emerg', 'icu', 'critical'], icon: 'fa-solid fa-truck-medical' },
  { keys: ['radio', 'imaging', 'mri', 'xray'], icon: 'fa-solid fa-x-ray' },
  { keys: ['lab', 'pathol'], icon: 'fa-solid fa-flask-vial' },
  { keys: ['skin', 'dermat'], icon: 'fa-solid fa-hand-dots' },
  { keys: ['audio', 'ear', 'ent'], icon: 'fa-solid fa-ear-listen' },
  { keys: ['psych', 'mental'], icon: 'fa-solid fa-head-side-brain' },
  { keys: ['physio', 'rehab'], icon: 'fa-solid fa-person-walking-with-cane' },
  { keys: ['diet', 'nutrit'], icon: 'fa-solid fa-apple-whole' },
];
function getIcon(name) {
  const l = (name || '').toLowerCase();
  for (const { keys, icon } of ICON_MAP) {
    if (keys.some((k) => l.includes(k))) return icon;
  }
  return 'fa-solid fa-hospital-user';
}

/* ─── Skeleton ──────────────────────────────────────────────────── */
const sk = (w, h, r = 8) => ({
  height: h,
  borderRadius: r,
  width: w,
  background: 'linear-gradient(90deg,#e8eef8 25%,#d4ddf5 50%,#e8eef8 75%)',
  backgroundSize: '700px 100%',
  animation: 'svcShimmer 1.5s infinite linear',
  marginBottom: 14,
});

/* ─── Hero image ────────────────────────────────────────────────── */
function HeroImage({ service }) {
  const [src, setSrc] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => {
    if (!service?.hasImage) return;
    let cancelled = false;
    fetch(`${BASE}/website/service/${service.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!cancelled && blob) {
          urlRef.current = URL.createObjectURL(blob);
          setSrc(urlRef.current);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [service?.id, service?.hasImage]);

  const icon = getIcon(service?.name);

  return (
    <div style={styles.heroImg}>
      {src ? (
        <img src={src} alt={service.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={styles.heroIconWrap}>
          <i className={icon} style={styles.heroIcon} />
        </div>
      )}
      <div style={styles.heroOverlay} />
    </div>
  );
}

/* ─── Sidebar: other services ───────────────────────────────────── */
function OtherServices({ services, currentId }) {
  const others = services.filter((s) => s.id !== currentId).slice(0, 8);
  return (
    <div style={styles.sideCard}>
      <div style={styles.sideCardHeader}>
        <i className="fa-solid fa-list-ul" style={{ marginRight: 8, color: '#076cec' }} />
        Other Services
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {others.map((s) => (
          <li key={s.id}>
            <Link to={`/service/${s.id}`} style={styles.sideLink}>
              <i className="fa-solid fa-angle-right" style={{ color: '#076cec', fontSize: 11, marginRight: 8, flexShrink: 0 }} />
              {s.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── Inline styles ─────────────────────────────────────────────── */
const styles = {
  page: { background: '#f4f8ff', minHeight: '100vh' },

  /* hero */
  heroWrap: {
    position: 'relative',
    borderRadius: 20,
    overflow: 'hidden',
    marginBottom: 36,
    boxShadow: '0 8px 32px rgba(7,108,236,.12)',
  },
  heroImg: {
    height: 380,
    position: 'relative',
    background: 'linear-gradient(135deg,#e8f0fe 0%,#c8deff 100%)',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(7,108,236,0) 40%, rgba(0,16,60,.65) 100%)',
  },
  heroIconWrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroIcon: { fontSize: 100, color: '#076cec', opacity: 0.3 },

  /* badges row inside hero */
  heroBadges: {
    position: 'absolute',
    bottom: 24,
    left: 28,
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 14px',
    borderRadius: 50,
    background: 'rgba(255,255,255,.18)',
    backdropFilter: 'blur(6px)',
    border: '1px solid rgba(255,255,255,.3)',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
  },

  /* main card */
  mainCard: {
    background: '#fff',
    borderRadius: 20,
    padding: '40px 44px',
    boxShadow: '0 4px 24px rgba(7,108,236,.07)',
    animation: 'svcFadeUp .4s ease both',
  },

  /* service title block */
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 18,
    paddingBottom: 28,
    marginBottom: 28,
    borderBottom: '1px solid #e8eef8',
  },
  titleIcon: {
    width: 62,
    height: 62,
    borderRadius: 14,
    background: 'linear-gradient(135deg,#076cec,#0458c4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 6px 18px rgba(7,108,236,.3)',
  },
  eyebrow: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#076cec',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  mainTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 8px',
    lineHeight: 1.25,
  },
  desc: {
    fontSize: 16,
    color: '#64748b',
    margin: 0,
    lineHeight: 1.6,
  },

  /* highlights row */
  highlights: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 36,
  },
  highlightCard: {
    background: '#f4f8ff',
    borderRadius: 14,
    padding: '18px 16px',
    textAlign: 'center',
    border: '1px solid #dde8f8',
  },
  hlIconWrap: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#076cec,#0458c4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 10px',
  },
  hlTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '0 0 2px' },
  hlSub: { fontSize: 12, color: '#64748b', margin: 0 },

  /* content divider */
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#076cec',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  divider: { borderTop: '2px solid #e8eef8', margin: '32px 0' },

  /* empty */
  empty: {
    textAlign: 'center',
    padding: '56px 24px',
    color: '#94a3b8',
  },

  /* CTA */
  ctaBox: {
    background: 'linear-gradient(135deg,#076cec 0%,#0458c4 100%)',
    borderRadius: 16,
    padding: '32px 36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 20,
    marginTop: 36,
  },
  ctaText: { color: '#fff' },
  ctaTitle: { fontSize: 22, fontWeight: 800, margin: '0 0 6px', color: '#fff' },
  ctaSub: { fontSize: 14, color: 'rgba(255,255,255,.8)', margin: 0 },
  ctaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '13px 28px',
    background: '#fff',
    color: '#076cec',
    fontWeight: 700,
    fontSize: 14,
    borderRadius: 8,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 14px rgba(0,0,0,.12)',
    transition: 'transform .2s, box-shadow .2s',
  },

  /* sidebar */
  sidebar: { display: 'flex', flexDirection: 'column', gap: 24 },
  sideCard: {
    background: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(7,108,236,.07)',
  },
  sideCardHeader: {
    padding: '16px 20px',
    background: 'linear-gradient(135deg,#076cec,#0458c4)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
  },
  sideLink: {
    display: 'flex',
    alignItems: 'center',
    padding: '11px 20px',
    fontSize: 14,
    color: '#334155',
    textDecoration: 'none',
    borderBottom: '1px solid #f0f4fb',
    transition: 'background .15s, color .15s',
  },
  contactCard: {
    background: 'linear-gradient(145deg,#076cec,#0d3b6e)',
    borderRadius: 16,
    padding: '28px 24px',
    textAlign: 'center',
    color: '#fff',
  },
  contactBtn: {
    display: 'inline-block',
    marginTop: 16,
    padding: '11px 26px',
    background: '#fff',
    color: '#076cec',
    fontWeight: 700,
    fontSize: 14,
    borderRadius: 8,
    textDecoration: 'none',
  },

  /* back link */
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    color: '#076cec',
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    marginBottom: 24,
  },
};

/* ─── Main component ────────────────────────────────────────────── */
const ServiceDetailsMain = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);
    setNotFound(false);
    setService(null);

    Promise.all([
      makeRequest({ route: `/website/service/public`, method: 'get' }),
    ])
      .then(([{ data }]) => {
        const all = Array.isArray(data) ? data : [];
        setAllServices(all);
        const found = all.find((s) => String(s.id) === String(id));
        if (found) setService(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const icon = getIcon(service?.name);

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <main style={styles.page}>
        <Breadcrumb title="Service Details" subTitle="Services" />
        <div className="container" style={{ paddingTop: 60, paddingBottom: 80 }}>
          <div className="row">
            <div className="col-xl-8 col-lg-8">
              <div style={styles.mainCard}>
                <div style={sk('40%', 28, 6)} />
                <div style={sk('70%', 20, 6)} />
                <div style={{ ...sk('100%', 14, 6), marginTop: 24 }} />
                <div style={sk('100%', 14, 6)} />
                <div style={sk('80%', 14, 6)} />
                <div style={sk('60%', 14, 6)} />
              </div>
            </div>
            <div className="col-xl-4 col-lg-4">
              <div style={sk('100%', 200, 16)} />
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ── Not found ── */
  if (notFound) {
    return (
      <main style={styles.page}>
        <Breadcrumb title="Service Details" subTitle="Services" />
        <div className="container" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 56, color: '#076cec', opacity: 0.4, display: 'block', marginBottom: 20 }} />
          <h3 style={{ color: '#0f172a', marginBottom: 12 }}>Service Not Found</h3>
          <p style={{ color: '#64748b', marginBottom: 28 }}>The service you're looking for doesn't exist or has been removed.</p>
          <Link to="/service" style={{ ...styles.ctaBtn, display: 'inline-flex' }}>
            <i className="fa-solid fa-arrow-left" /> Back to Services
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <Breadcrumb title={service.name} subTitle="Services" />

      <div className="container" style={{ paddingTop: 48, paddingBottom: 80 }}>
        {/* Back */}
        <Link to="/service" style={styles.backLink}>
          <i className="fa-solid fa-arrow-left" /> Back to All Services
        </Link>

        <div className="row">
          {/* ─── Main column ─────────────────────────────── */}
          <div className="col-xl-8 col-lg-8 mb-40">
            {/* Hero image */}
            <div style={styles.heroWrap}>
              <HeroImage service={service} />
              {/* Floating badges */}
              <div style={styles.heroBadges}>
                <span style={styles.badge}>
                  <i className="fa-solid fa-circle-check" /> Active Service
                </span>
                <span style={styles.badge}>
                  <i className="fa-solid fa-clock" /> Available 24 / 7
                </span>
              </div>
            </div>

            {/* Title block */}
            <div style={styles.mainCard}>
              <div style={styles.titleRow}>
                <div style={styles.titleIcon}>
                  <i className={icon} style={{ fontSize: 26, color: '#fff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={styles.eyebrow}>Maroof International Hospital</span>
                  <h1 style={styles.mainTitle}>{service.name}</h1>
                  {service.description && <p style={styles.desc}>{service.description}</p>}
                </div>
              </div>

              {/* Quick highlights */}
              <div style={styles.highlights}>
                <div style={styles.highlightCard}>
                  <div style={styles.hlIconWrap}>
                    <i className="fa-solid fa-user-doctor" style={{ color: '#fff', fontSize: 18 }} />
                  </div>
                  <p style={styles.hlTitle}>Expert Clinicians</p>
                  <p style={styles.hlSub}>Board-certified specialists</p>
                </div>
                <div style={styles.highlightCard}>
                  <div style={styles.hlIconWrap}>
                    <i className="fa-solid fa-shield-halved" style={{ color: '#fff', fontSize: 18 }} />
                  </div>
                  <p style={styles.hlTitle}>Accredited Care</p>
                  <p style={styles.hlSub}>JCI & ISO certified</p>
                </div>
                <div style={styles.highlightCard}>
                  <div style={styles.hlIconWrap}>
                    <i className="fa-solid fa-truck-medical" style={{ color: '#fff', fontSize: 18 }} />
                  </div>
                  <p style={styles.hlTitle}>24 / 7 Available</p>
                  <p style={styles.hlSub}>Round-the-clock service</p>
                </div>
              </div>

              {/* Rich content */}
              {service.content ? (
                <>
                  <p style={styles.sectionLabel}>
                    <i className="fa-solid fa-file-medical" />
                    Service Overview
                  </p>
                  <div
                    className="svc-html"
                    dangerouslySetInnerHTML={{ __html: service.content }}
                  />
                </>
              ) : (
                <div style={styles.empty}>
                  <i className="fa-solid fa-circle-info" style={{ fontSize: 42, display: 'block', marginBottom: 14, opacity: 0.35 }} />
                  <p style={{ margin: 0, fontSize: 16 }}>
                    Detailed information about this service will be available soon.
                  </p>
                </div>
              )}

              {/* CTA banner */}
              <div style={styles.ctaBox}>
                <div style={styles.ctaText}>
                  <p style={styles.ctaTitle}>Ready to get started?</p>
                  <p style={styles.ctaSub}>Book an appointment with our specialists today.</p>
                </div>
                <Link to="/appointment" style={styles.ctaBtn}>
                  <i className="fa-solid fa-calendar-check" /> Book Appointment
                </Link>
              </div>
            </div>
          </div>

          {/* ─── Sidebar ─────────────────────────────────── */}
          <div className="col-xl-4 col-lg-4 mb-40">
            <div style={styles.sidebar}>
              {/* Contact card */}
              <div style={styles.contactCard}>
                <i className="fa-solid fa-phone-volume" style={{ fontSize: 34, marginBottom: 14, display: 'block' }} />
                <h4 style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: '0 0 6px' }}>Need Assistance?</h4>
                <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 13, margin: '0 0 4px' }}>Our patient services team is</p>
                <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 13, margin: '0 0 16px' }}>available round the clock.</p>
                <a href="tel:+92519220022" style={styles.contactBtn}>
                  <i className="fa-solid fa-phone" style={{ marginRight: 6 }} />
                  Call Us Now
                </a>
                <Link to="/appointment" style={{ ...styles.contactBtn, display: 'block', marginTop: 10, background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }}>
                  <i className="fa-solid fa-calendar-plus" style={{ marginRight: 6 }} />
                  Book Appointment
                </Link>
              </div>

              {/* Other services */}
              {allServices.length > 1 && (
                <OtherServices services={allServices} currentId={service.id} />
              )}

              {/* Info card */}
              <div style={{ ...styles.sideCard }}>
                <div style={styles.sideCardHeader}>
                  <i className="fa-solid fa-circle-info" style={{ marginRight: 8 }} />
                  Quick Info
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[
                    { icon: 'fa-solid fa-location-dot', label: 'Location', value: 'Maroof International Hospital, Islamabad' },
                    { icon: 'fa-solid fa-clock', label: 'Hours', value: '24 hours / 7 days' },
                    { icon: 'fa-solid fa-phone', label: 'Helpline', value: '+92 51 922 0022' },
                  ].map(({ icon: ic, label, value }) => (
                    <li key={label} style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid #f0f4fb' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#edf2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={ic} style={{ fontSize: 14, color: '#076cec' }} />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</p>
                        <p style={{ margin: 0, fontSize: 13, color: '#334155', fontWeight: 500 }}>{value}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ServiceDetailsMain;
